// What each field of a dataset holds, worked out from the screen's copy in
// one pass (the server is not asked).
//
// Promise:
// - `filled` counts the rows whose value is there (not null, not missing).
// - A number field: its least, greatest, mean and total (values that are not
//   numbers are left out, as the query's aggregates do).
// - A date or date-and-time field: the first and the last value (as written;
//   dates compare as text, the ISO form orders them).
// - A text field: how many different values; a checkbox: how many are on.
import type { DatasetField, DatasetRow } from "@trameria/core/datasets-shared";

export interface FieldStats {
  filled: number;
  min?: number;
  max?: number;
  mean?: number;
  sum?: number;
  first?: string;
  last?: string;
  distinct?: number;
  on?: number;
}

export interface Histogram {
  min: number;
  max: number;
  /** How many values fall in each of the equal bins from min to max. */
  counts: number[];
}

/**
 * How a number field's values spread: `bins` equal bins from the least to
 * the greatest (values that are not numbers left out); null without any.
 */
export function histogram(
  rows: Iterable<DatasetRow>,
  field: string,
  bins = 20,
): Histogram | null {
  const values: number[] = [];
  for (const row of rows) {
    const v = row[field];
    if (typeof v === "number" && Number.isFinite(v)) values.push(v);
  }
  if (values.length === 0) return null;
  let min = values[0] as number;
  let max = min;
  for (const v of values) {
    if (v < min) min = v;
    if (v > max) max = v;
  }
  // One value throughout: one bar.
  const n = max === min ? 1 : bins;
  const counts = new Array<number>(n).fill(0);
  const width = (max - min) / n;
  for (const v of values) {
    const i = width === 0 ? 0 : Math.min(n - 1, Math.floor((v - min) / width));
    counts[i] = (counts[i] ?? 0) + 1;
  }
  return { min, max, counts };
}

export function fieldStats(
  rows: Iterable<DatasetRow>,
  fields: readonly DatasetField[],
): Map<string, FieldStats> {
  const out = new Map<string, FieldStats>();
  const seen = new Map<string, Set<unknown>>();
  const numbers = new Map<string, number>();
  for (const f of fields) {
    out.set(f.name, { filled: 0 });
    if (f.type === "string") seen.set(f.name, new Set());
  }
  for (const row of rows)
    for (const f of fields) {
      const v = row[f.name];
      if (v === null || v === undefined) continue;
      const s = out.get(f.name) as FieldStats;
      s.filled++;
      if (f.type === "number" && typeof v === "number" && Number.isFinite(v)) {
        s.min = s.min === undefined ? v : Math.min(s.min, v);
        s.max = s.max === undefined ? v : Math.max(s.max, v);
        s.sum = (s.sum ?? 0) + v;
        numbers.set(f.name, (numbers.get(f.name) ?? 0) + 1);
      } else if (
        (f.type === "date" || f.type === "datetime") &&
        typeof v === "string"
      ) {
        if (s.first === undefined || v < s.first) s.first = v;
        if (s.last === undefined || v > s.last) s.last = v;
      } else if (f.type === "string") seen.get(f.name)?.add(v);
      else if (f.type === "boolean" && v === true) s.on = (s.on ?? 0) + 1;
    }
  for (const f of fields) {
    const s = out.get(f.name) as FieldStats;
    const n = numbers.get(f.name);
    if (n && s.sum !== undefined) s.mean = s.sum / n;
    if (f.type === "string") s.distinct = seen.get(f.name)?.size ?? 0;
    if (f.type === "boolean") s.on ??= 0;
  }
  return out;
}
